import { useTranslation } from "react-i18next";
import { formatDateShort } from "@/shared/lib/format-date";
import { formatMoney, moneyFromMajorNumber } from "./money";
import type { OwnerPayoutProjection } from "./types";

interface OwnerPayoutProjectionCardProps {
  readonly projection: OwnerPayoutProjection;
  readonly locale: string;
}

export function OwnerPayoutProjectionCard({ projection, locale }: OwnerPayoutProjectionCardProps) {
  const { t } = useTranslation();

  // API-FIN-8: amounts are numbers in MAJOR units (dollars). `currency` is null
  // only when there are no unsettled accruals (amounts are then 0).
  const formatMajor = (amount: number): string =>
    projection.currency ? formatMoney(moneyFromMajorNumber(amount, projection.currency)) : "—";

  return (
    <section
      data-testid="owner-payout-projection"
      className="grid gap-4 rounded-lg border border-border bg-card p-6 sm:grid-cols-2 lg:grid-cols-4"
    >
      <Metric
        label={t("finance.ownerPayouts.projection.nextAmount")}
        value={formatMajor(projection.nextPayoutAmount)}
      />
      <Metric
        label={t("finance.ownerPayouts.projection.nextDate")}
        value={formatDateShort(projection.nextPayoutDate, locale) || "—"}
      />
      <Metric
        label={t("finance.ownerPayouts.projection.periodAccrued")}
        value={formatMajor(projection.currentPeriodAccrued)}
      />
      <Metric
        label={t("finance.ownerPayouts.projection.periodCheckins")}
        value={String(projection.currentPeriodCheckinCount)}
      />
    </section>
  );
}

function Metric({ label, value }: { readonly label: string; readonly value: string }) {
  return (
    <div className="space-y-1">
      <p className="font-sans text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </p>
      <p className="font-display text-xl">{value}</p>
    </div>
  );
}
